import Decimal from "decimal.js";

/**
 * Estimates the average trade price based on a given order book and input amount.
 *
 * This function iterates through the order book from the best available price,
 * consuming liquidity until the requested amount is fulfilled. The resulting
 * average price is computed as a volume-weighted average.
 *
 * @param {Decimal} amountIn - The amount of the asset to be traded. Can be a quote or base amount.
 * @param {[number, number][]} orderBook - The order book represented as an array of price levels,
 *     where each entry consists of a price and an available amount at that price.
 * @param {boolean} isQuote - Whether the amountIn is a quote amount.
 * @returns {Decimal} - The estimated average trade price. Returns 0 if no liquidity is available.
 */
export const estimateExecutedPriceFromOrderBook = (
  amountIn: Decimal,
  orderBook: [number, number][],
  isQuote: boolean,
): Decimal => {
  let totalQuoteAmount = new Decimal(0);
  // totalBaseAmount tracks the actual amount that could be traded given the available liquidity
  // It may be less than amountIn if there's not enough liquidity in the order book
  // Used to calculate the volume-weighted average price (VWAP) as totalQuoteAmount/totalBaseAmount
  let totalBaseAmount = new Decimal(0);
  let remainingQuoteOrBase = amountIn;

  for (const [price, baseAvailableAmount] of orderBook) {
    const decimalPrice = new Decimal(price);
    const decimalBaseAvailable = new Decimal(baseAvailableAmount);
    const baseRemainingAmount = isQuote ? toBase(remainingQuoteOrBase, decimalPrice) : remainingQuoteOrBase;
    const baseAmountToTrade = Decimal.min(baseRemainingAmount, decimalBaseAvailable);
    totalQuoteAmount = totalQuoteAmount.plus(toQuote(baseAmountToTrade, decimalPrice));
    totalBaseAmount = totalBaseAmount.plus(baseAmountToTrade);
    remainingQuoteOrBase = remainingQuoteOrBase.minus(
      isQuote ? toQuote(baseAmountToTrade, decimalPrice) : baseAmountToTrade,
    );

    if (remainingQuoteOrBase.lte(0)) break;
  }

  return totalBaseAmount.gt(0) ? totalQuoteAmount.div(totalBaseAmount) : new Decimal(0);
};

export const toQuote = (baseAmount: Decimal, price: Decimal): Decimal => {
  return baseAmount.mul(price);
};

export const toBase = (quoteAmount: Decimal, price: Decimal): Decimal => {
  return quoteAmount.div(price);
};
